import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { LANGUAGES } from "@/i18n/translations";
import { SITE } from "@/constants/site";

const NAV_IDS = ["home", "about", "services", "gallery", "pricing", "booking", "contact"];

export default function Navigation() {
    const { lang, setLang, t } = useLanguage();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [active, setActive] = useState("home");

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const sections = NAV_IDS.map((id) => document.getElementById(id)).filter(Boolean);
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActive(e.target.id);
                });
            },
            { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
        );
        sections.forEach((s) => obs.observe(s));
        return () => obs.disconnect();
    }, []);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpen(false);
    };

    return (
        <motion.header
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                scrolled ? "bg-white/90 backdrop-blur-md border-b border-gold/20" : "bg-gradient-to-b from-black/40 to-transparent"
            }`}
            data-testid="site-nav"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="flex items-center justify-between h-16 lg:h-20">
                    {/* Logo */}
                    <button
                        onClick={() => scrollTo("home")}
                        className="flex items-center gap-3"
                        data-testid="nav-logo"
                    >
                        <img
                            src={SITE.logo}
                            alt="Frizerka logo"
                            className="h-10 w-10 lg:h-11 lg:w-11 rounded-full object-cover ring-1 ring-gold/40"
                        />
                        <div className="hidden sm:flex flex-col leading-none">
                            <span className={`font-display italic text-[1.05rem] transition-colors duration-500 ${scrolled ? "text-ink" : "text-white"}`}>Frizerka</span>
                            <span className={`text-[0.6rem] tracking-[0.3em] uppercase mt-1 transition-colors duration-500 ${scrolled ? "text-gold-deep" : "text-gold-soft"}`}>
                                Hair by Tijana
                            </span>
                        </div>
                    </button>

                    {/* Desktop nav */}
                    <nav className="hidden lg:flex items-center gap-9">
                        {NAV_IDS.map((id) => (
                            <button
                                key={id}
                                onClick={() => scrollTo(id)}
                                data-active={active === id}
                                data-testid={`nav-link-${id}`}
                                className={`nav-link text-[0.78rem] tracking-[0.22em] uppercase font-medium transition-colors duration-500 ${
                                    scrolled ? "text-ink" : "text-white"
                                }`}
                            >
                                {t.nav[id]}
                            </button>
                        ))}
                    </nav>

                    {/* Right cluster */}
                    <div className="flex items-center gap-2 lg:gap-3">
                        {/* Language — visible on all screens */}
                        <div className="relative">
                            <button
                                onClick={() => setLangOpen((v) => !v)}
                                onBlur={() => setTimeout(() => setLangOpen(false), 150)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-full border transition-colors ${
                                    scrolled
                                        ? "border-gold/40 hover:border-gold text-ink"
                                        : "border-white/40 hover:border-white text-white"
                                } text-[0.72rem] tracking-[0.2em] uppercase`}
                                data-testid="lang-toggle"
                            >
                                <span className="text-base leading-none" aria-hidden="true">
                                    {LANGUAGES.find((l) => l.code === lang)?.flag}
                                </span>
                                <span className="normal-case">{LANGUAGES.find((l) => l.code === lang)?.short || lang.toUpperCase()}</span>
                            </button>
                            <AnimatePresence>
                                {langOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -6 }}
                                        transition={{ duration: 0.25 }}
                                        className="absolute right-0 mt-2 min-w-[180px] bg-white border border-gold/25 rounded-xl shadow-[0_20px_50px_-12px_rgba(201,162,39,0.15)] overflow-hidden"
                                        data-testid="lang-menu"
                                    >
                                        {LANGUAGES.map((L) => (
                                            <button
                                                key={L.code}
                                                onMouseDown={() => { setLang(L.code); setLangOpen(false); }}
                                                data-testid={`lang-option-${L.code}`}
                                                className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-cream transition-colors"
                                            >
                                                <span className="flex items-center gap-3">
                                                    <span className="text-base leading-none" aria-hidden="true">{L.flag}</span>
                                                    <span className="font-sans text-ink">{L.label}</span>
                                                </span>
                                                {lang === L.code && <Check className="w-4 h-4 text-gold" />}
                                            </button>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <button
                            onClick={() => scrollTo("booking")}
                            className="hidden lg:inline-flex btn-gold"
                            data-testid="nav-cta-book"
                        >
                            {t.misc.book_now}
                        </button>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setOpen(true)}
                            className={`lg:hidden p-2 transition-colors ${scrolled ? "text-ink" : "text-white"}`}
                            data-testid="nav-mobile-open"
                            aria-label="Open menu"
                        >
                            <Menu className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile drawer */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] bg-white"
                        data-testid="nav-mobile-drawer"
                    >
                        <div className="flex items-center justify-between px-6 h-20 border-b border-gold/20">
                            <img src={SITE.logo} alt="" className="h-11 w-11 rounded-full ring-1 ring-gold/40" />
                            <button onClick={() => setOpen(false)} className="p-2" data-testid="nav-mobile-close">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="px-8 py-10 flex flex-col gap-5">
                            {NAV_IDS.map((id, i) => (
                                <motion.button
                                    key={id}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i, duration: 0.5 }}
                                    onClick={() => scrollTo(id)}
                                    className="text-left font-display text-3xl text-ink"
                                    data-testid={`mnav-link-${id}`}
                                >
                                    <span className="serif-italic text-gold text-sm mr-3">0{i + 1}</span>
                                    {t.nav[id]}
                                </motion.button>
                            ))}

                            {/* Language picker inside mobile drawer */}
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.05 * NAV_IDS.length, duration: 0.5 }}
                                className="mt-4 pt-5 border-t border-gold/20"
                            >
                                <div className="eyebrow mb-3">Sprache</div>
                                <div className="flex flex-wrap gap-2">
                                    {LANGUAGES.map((L) => (
                                        <button
                                            key={L.code}
                                            onClick={() => setLang(L.code)}
                                            data-testid={`mnav-lang-${L.code}`}
                                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border transition-colors ${
                                                lang === L.code
                                                    ? "bg-gold text-white border-gold"
                                                    : "border-gold/40 text-ink hover:border-gold"
                                            } text-xs tracking-[0.2em] uppercase`}
                                        >
                                            <span className="text-base leading-none">{L.flag}</span>
                                            <span className="normal-case">{L.short || L.code.toUpperCase()}</span>
                                        </button>
                                    ))}
                                </div>
                            </motion.div>

                            <div className="mt-6">
                                <button onClick={() => scrollTo("booking")} className="btn-gold w-full">
                                    {t.misc.book_now}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
